import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { ApiError } from '@/shared/api/client';
import { DMS_PRODUCTLINES } from '@/shared/constants';
import { Dialog } from '@/shared/ui/Dialog';
import { Select } from '@/shared/ui/Select';
import { TokenInput } from '@/shared/ui/TokenInput';
import {
  createSearchAttribute,
  searchAttributesQueryKey,
  updateSearchAttribute,
  type SearchAttribute,
  type SearchAttributeInput,
} from '../api';
import {
  normalizeLabel,
  searchAttributeFormSchema,
  type SearchAttributeFormValues,
} from './search-attribute-form';

interface SearchAttributeDialogProps {
  item: SearchAttribute | null;
  onClose: () => void;
}

const sortValues = (values: readonly string[]) =>
  [...values].sort((left, right) => left.localeCompare(right));

const getErrorMessage = (reason: unknown): string => {
  if (!(reason instanceof ApiError)) return 'Failed to save filter';

  if (typeof reason.body === 'string') return reason.body;

  if (
    reason.body &&
    typeof reason.body === 'object' &&
    'message' in reason.body &&
    typeof reason.body.message === 'string'
  ) {
    return reason.body.message;
  }

  return 'Failed to save filter';
};

export function SearchAttributeDialog({
  item,
  onClose,
}: Readonly<SearchAttributeDialogProps>) {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SearchAttributeFormValues>({
    resolver: zodResolver(searchAttributeFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: item?.name ? normalizeLabel(item.name) : '',
      productLines: sortValues(item?.productLines ?? []),
      allowedValues: sortValues(
        (item?.allowedValues ?? []).map(({ value }) => normalizeLabel(value)),
      ),
    },
  });
  const mutation = useMutation({
    mutationFn: (data: SearchAttributeInput) =>
      item ? updateSearchAttribute(item.id, data) : createSearchAttribute(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: searchAttributesQueryKey,
      });
      onClose();
    },
    onError: (reason) => setErrorMessage(getErrorMessage(reason)),
  });

  const submit = (values: SearchAttributeFormValues) => {
    setErrorMessage(null);
    mutation.mutate({
      name: normalizeLabel(values.name),
      productLines: sortValues(values.productLines),
      allowedValues: sortValues(values.allowedValues.map(normalizeLabel)),
    });
  };

  const title = item ? 'Edit a filter' : 'Add a filter';

  return (
    <Dialog
      title={title}
      onClose={onClose}
      width="600px"
      contentOverflow="visible"
    >
      <form
        className="form popin-form"
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <label className="field-label" htmlFor="search-attribute-name">
          Name
        </label>
        <input
          id="search-attribute-name"
          className={`field mb-1 ${errors.name ? 'invalid' : ''}`}
          placeholder="Name"
          {...register('name')}
        />
        <small hidden={!errors.name}>{errors.name?.message}</small>
        <label className="field-label" htmlFor="search-attribute-product-lines">
          Product lines
        </label>
        <div
          className={`field field-selector mb-1 ${errors.productLines ? 'invalid' : ''}`}
        >
          <Controller
            name="productLines"
            control={control}
            render={({ field }) => (
              <Select
                id="search-attribute-product-lines"
                multiple
                hideSelected
                options={DMS_PRODUCTLINES}
                value={field.value}
                onChange={(values) => field.onChange(sortValues(values))}
                placeholder="--Select product lines--"
              />
            )}
          />
        </div>
        <small hidden={!errors.productLines}>
          {errors.productLines?.message}
        </small>
        <label
          className="field-label"
          htmlFor="search-attribute-allowed-values"
        >
          Allowed filter values
        </label>
        <div
          className={`field field-selector mb-1 ${errors.allowedValues ? 'invalid' : ''}`}
        >
          <Controller
            name="allowedValues"
            control={control}
            render={({ field }) => (
              <TokenInput
                id="search-attribute-allowed-values"
                value={field.value}
                onChange={(values) =>
                  field.onChange(sortValues(values.map(normalizeLabel)))
                }
                placeholder="--Type an allowed filter value and press Enter--"
              />
            )}
          />
        </div>
        <small hidden={!errors.allowedValues}>
          {errors.allowedValues?.message ?? errors.allowedValues?.[0]?.message}
        </small>
        {errorMessage && (
          <div className="alert mt-3" role="alert">
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-6 flex justify-center">
          <button
            className="button button-form disabled:opacity-50"
            type="submit"
            disabled={mutation.isPending || !isValid}
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
